import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
} from 'react-native';
import { Text } from '@/components/common/text';
import { useDesktopColors } from '@/constants/residents/desktop/color';
import {
  PaymentFormInput,
  PaymentCheckbox,
  PaymentSecurityNote,
  PaymentFormFooter,
} from '@/components/residents/payments';
import { 
  useAddBankAccountMutation,
} from '@/store/api';
import usePaymentForm from '@/features/payments/hooks/usePaymentForm';
import { usePaymentData } from '@/features/payments/hooks/usePaymentData';
import { 
  ROUTING_NUMBER_LENGTH,
  MAX_ACCOUNT_NUMBER_LENGTH,
  isValidAbaRoutingNumber,
  sanitizeRoutingNumber,
  sanitizeAccountNumber,
} from '@/utils/residents/payment/ach';

interface Props {
  leaseId: number;
  onSuccess: () => void;
  onBack: () => void;
  showMakeDefault?: boolean;
  showInstructions?: boolean;
}

interface ACHFormData {
  name: string;
  routingNumber: string;
  accountNumber: string;
  confirmAccountNumber: string;
  isDefault: boolean;
}

export const ACHDetailsForm: React.FC<Props> = ({
  leaseId,
  onSuccess,
  onBack,
  showMakeDefault = false,
  showInstructions = false,
}) => {
  const DESKTOP_COLORS = useDesktopColors();
  // Get payment data for refreshing after success
  const { refreshPaymentMethods } = usePaymentData();
  
  const {
    formData,
    errors,
    isSubmitting,
    updateField,
    setFieldError,
    submitForm,
  } = usePaymentForm<ACHFormData>({
    initialData: {
      name: '',
      routingNumber: '',
      accountNumber: '',
      confirmAccountNumber: '',
      isDefault: false,
    },
    onSuccess: () => {
      // Refresh payment methods data after success
      refreshPaymentMethods();
      onSuccess();
    },
    successMessage: 'Bank account added successfully',
  });
  
  const [isRoutingNumberValid, setIsRoutingNumberValid] = useState(false);
  const [showAccountNumberWarning, setShowAccountNumberWarning] = useState(false);
  
  const [addBankAccount, { isLoading: isAddingAccount }] = useAddBankAccountMutation();
  
  const isLoading = isAddingAccount;

  // Validation helpers now imported from utils

  const validateForm = (data: ACHFormData) => {
    const newErrors: any = {};
    
    if (!data.name.trim()) {
      newErrors.name = 'Required';
    }
    
    if (!data.routingNumber) {
      newErrors.routingNumber = 'Required';
    } else if (data.routingNumber.length !== ROUTING_NUMBER_LENGTH) {
      newErrors.routingNumber = 'Invalid routing number';
    } else if (!isValidAbaRoutingNumber(data.routingNumber)) {
      newErrors.routingNumber = 'Invalid routing number';
    }
    
    if (!data.accountNumber) {
      newErrors.accountNumber = 'Required';
    } else if (data.accountNumber.length > MAX_ACCOUNT_NUMBER_LENGTH) {
      newErrors.accountNumber = 'Invalid Account number';
    }
    
    if (!data.confirmAccountNumber) {
      newErrors.confirmAccountNumber = 'Required';
    } else if (data.accountNumber !== data.confirmAccountNumber) {
      newErrors.confirmAccountNumber = 'Does not match';
    }
    
    return newErrors;
  };

  // Update validation states
  useEffect(() => {
    setIsRoutingNumberValid(isValidAbaRoutingNumber(formData.routingNumber));
    setShowAccountNumberWarning(
      !!formData.confirmAccountNumber && 
      formData.accountNumber === formData.confirmAccountNumber &&
      formData.accountNumber.length > 0
    );
  }, [formData.routingNumber, formData.accountNumber, formData.confirmAccountNumber]);

  const handleInputChange = (field: keyof ACHFormData, value: string) => {
    let processedValue = value;
    
    // Process routing number - digits only, max 9
    if (field === 'routingNumber') {
      processedValue = sanitizeRoutingNumber(value);
    }
    if (field === 'accountNumber' || field === 'confirmAccountNumber') {
      processedValue = sanitizeAccountNumber(value);
    }
    
    updateField(field, processedValue);
  };

  const handleSubmit = async () => {
    await submitForm(async (data) => {
      // Tokenize via gateway util
      const { tokenizeAch } = await import('@/utils/residents/payment/paymentsGateway');
      const token = await tokenizeAch({ routingNumber: data.routingNumber, accountNumber: data.accountNumber });

      // Add bank account
      return await addBankAccount({
        name: data.name,
        token,
        isDefault: data.isDefault,
        accttype: 'ECHK', // ACH checking account type
      }).unwrap();
    }, validateForm);
  };

  const canSubmit = isRoutingNumberValid && showAccountNumberWarning && formData.name.trim();

  return (
    <View style={styles.container}>
      {showInstructions && (
        <Text style={[styles.instructions, { color: DESKTOP_COLORS.TEXT_PRIMARY }]}>
          Please enter your checking account details.
        </Text>
      )}
      
      <View style={styles.form}>
        <PaymentFormInput
          label="Name"
          value={formData.name}
          onChangeText={(value) => handleInputChange('name', value)}
          placeholder="Account name"
          error={errors.name}
          autoCapitalize="words"
        />

        <PaymentFormInput
          label="Routing Number"
          value={formData.routingNumber}
          onChangeText={(value) => handleInputChange('routingNumber', value)}
          placeholder="Routing number"
          error={errors.routingNumber}
          keyboardType="numeric"
          maxLength={ROUTING_NUMBER_LENGTH}
          showIcon={formData.routingNumber.length > 0 ? (isRoutingNumberValid ? 'check' : 'warning') : 'none'}
        />

        <PaymentFormInput
          label="Account Number"
          value={formData.accountNumber}
          onChangeText={(value) => handleInputChange('accountNumber', value)}
          placeholder="Account number"
          error={errors.accountNumber}
          keyboardType="numeric"
          maxLength={MAX_ACCOUNT_NUMBER_LENGTH}
        />

        <PaymentFormInput
          label="Confirm Account Number"
          value={formData.confirmAccountNumber}
          onChangeText={(value) => handleInputChange('confirmAccountNumber', value)}
          placeholder="Confirm account number"
          error={errors.confirmAccountNumber}
          keyboardType="numeric"
          maxLength={MAX_ACCOUNT_NUMBER_LENGTH}
          showIcon={formData.confirmAccountNumber.length > 0 ? (showAccountNumberWarning ? 'check' : 'warning') : 'none'}
        />

        {showMakeDefault && (
          <PaymentCheckbox
            checked={formData.isDefault}
            onToggle={() => updateField('isDefault', !formData.isDefault)}
            label="Make this my default payment method"
          />
        )}

        <PaymentSecurityNote />
      </View>

      <PaymentFormFooter
        onBack={onBack}
        onSubmit={handleSubmit}
        submitDisabled={!canSubmit}
        submitLoading={isSubmitting || isLoading}
      />
    </View>
  );
};


const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  instructions: {
    fontSize: 16,
    marginBottom: 24,
  },
  form: {
    flex: 1,
  },
});

export default ACHDetailsForm;
